import api from '../api';
const createId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const clone = (value) => {
  if (value === undefined) return undefined;
  return JSON.parse(JSON.stringify(value));
};

const parseFlexibleDateInput = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;

  const yearMatch = raw.match(/^(\d{4})$/);
  if (yearMatch) {
    return { year: Number(yearMatch[1]), month: 1, day: 1, precision: 'year' };
  }

  const monthYearMatch = raw.match(/^(?:(\d{1,2})[/-](\d{4})|(\d{4})-(\d{1,2}))$/);
  if (monthYearMatch) {
    const month = Number(monthYearMatch[1] || monthYearMatch[4]);
    const year = Number(monthYearMatch[2] || monthYearMatch[3]);
    if (month >= 1 && month <= 12) {
      return { year, month, day: 1, precision: 'month' };
    }
  }

  const dateMatch = raw.match(/^(?:(\d{4})-(\d{1,2})-(\d{1,2})|(\d{1,2})[/-](\d{1,2})[/-](\d{4}))$/);
  if (dateMatch) {
    const year = Number(dateMatch[1] || dateMatch[6]);
    const month = Number(dateMatch[2] || dateMatch[5]);
    const day = Number(dateMatch[3] || dateMatch[4]);
    const date = new Date(Date.UTC(year, month - 1, day));
    if (
      date.getUTCFullYear() === year
      && date.getUTCMonth() === month - 1
      && date.getUTCDate() === day
    ) {
      return { year, month, day, precision: 'day' };
    }
  }

  return null;
};

const getFlexibleDatePrecision = (value, fallback = null) =>
  parseFlexibleDateInput(value)?.precision || fallback;

const toDateISOString = (value) => {
  if (!value) return null;
  const parsed = parseFlexibleDateInput(value);
  if (parsed) {
    return new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day)).toISOString();
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString();
};

const addMaintenancePeriod = (value, amount, unit) => {
  const numeric = Math.max(1, Number(amount) || 1);
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return new Date().toISOString();
  if (unit === 'YEARS') date.setFullYear(date.getFullYear() + numeric);
  else if (unit === 'MONTHS') date.setMonth(date.getMonth() + numeric);
  else if (unit === 'WEEKS') date.setDate(date.getDate() + numeric * 7);
  else date.setDate(date.getDate() + numeric);
  return date.toISOString();
};

const sanitizeAttachments = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((attachment) => attachment && typeof attachment === 'object')
    .map((attachment) => ({
      id: attachment?.id || createId(),
      name: attachment?.name?.trim() || 'Anexo',
      url: attachment?.url || '',
      type: attachment?.type?.trim() || 'application/octet-stream',
      size: Number(attachment?.size) || 0,
      uploadedAt: toDateISOString(attachment?.uploadedAt) || new Date().toISOString(),
    }));
};

const STORAGE_KEY = 'bpm.asset-management.v1';

const seedCategories = [
  {
    id: createId(),
    name: 'Infraestrutura de TI',
    description: 'Servidores, equipamentos de rede e ativos essenciais de tecnologia.',
  },
  {
    id: createId(),
    name: 'Equipamentos Operacionais',
    description: 'Máquinas, veículos e ferramentas utilizadas nas operações diárias.',
  },
  {
    id: createId(),
    name: 'Instalações',
    description: 'Benfeitorias e mobiliários permanentes.',
  },
];

const seedLocations = [
  {
    id: createId(),
    name: 'Sede - Centro Administrativo',
    description: 'Prédio principal da organização.',
    responsible: 'Ana Oliveira',
    contact: 'ana.oliveira@meunegocio.com',
  },
  {
    id: createId(),
    name: 'Filial Operacional',
    description: 'Unidade responsável pela produção.',
    responsible: 'Carlos Santos',
    contact: '(11) 99999-1000',
  },
  {
    id: createId(),
    name: 'Data Center',
    description: 'Ambiente dedicado aos servidores e equipamentos críticos.',
    responsible: 'Equipe de Infraestrutura',
    contact: 'infra@meunegocio.com',
  },
];

const seedAssets = [
  {
    id: createId(),
    name: 'Servidor de virtualização',
    serialNumber: 'SVR-92384',
    brand: 'Dell',
    tag: 'PAT-001',
    status: 'OPERATING',
    categoryId: seedCategories[0].id,
    locationId: seedLocations[2].id,
    acquisitionDate: '2022-03-15T00:00:00.000Z',
    acquisitionDatePrecision: 'day',
    acquisitionCost: 98500,
    depreciationRate: 15,
    usefulLifeMonths: 60,
    warrantyEnd: '2025-03-15T00:00:00.000Z',
    custodian: 'Bruno Lima',
    supplier: 'TechData',
    entryOrigin: 'Compra',
    invoiceNumber: 'NF-2022-001',
    disposalDate: null,
    disposalReason: '',
    notes:
      'Servidor responsável pela hospedagem da camada de virtualização das aplicações críticas.',
    attachments: [],
    createdAt: new Date().toISOString(),
    lastUpdatedAt: new Date().toISOString(),
  },
  {
    id: createId(),
    name: 'Empilhadeira elétrica',
    serialNumber: 'EMP-4421X',
    brand: 'Toyota',
    tag: 'PAT-014',
    status: 'MAINTENANCE',
    categoryId: seedCategories[1].id,
    locationId: seedLocations[1].id,
    acquisitionDate: '2020-09-10T00:00:00.000Z',
    acquisitionDatePrecision: 'day',
    acquisitionCost: 74000,
    depreciationRate: 10,
    usefulLifeMonths: 96,
    warrantyEnd: '2024-09-10T00:00:00.000Z',
    custodian: 'Luciana Prado',
    supplier: 'LiftMax',
    entryOrigin: 'Compra',
    invoiceNumber: 'NF-2020-014',
    disposalDate: null,
    disposalReason: '',
    notes:
      'Ativo fundamental para movimentação de cargas pesadas. Necessita de inspeção mensal de segurança.',
    attachments: [],
    createdAt: new Date().toISOString(),
    lastUpdatedAt: new Date().toISOString(),
  },
  {
    id: createId(),
    name: 'Sistema de climatização da sede',
    serialNumber: 'HVAC-2080',
    brand: 'Carrier',
    tag: 'PAT-031',
    status: 'OPERATING',
    categoryId: seedCategories[2].id,
    locationId: seedLocations[0].id,
    acquisitionDate: '2019-01-20T00:00:00.000Z',
    acquisitionDatePrecision: 'day',
    acquisitionCost: 125000,
    depreciationRate: 8,
    usefulLifeMonths: 120,
    warrantyEnd: '2023-01-20T00:00:00.000Z',
    custodian: 'Equipe de Facilities',
    supplier: 'ClimaTech',
    entryOrigin: 'Projeto interno',
    invoiceNumber: '',
    disposalDate: null,
    disposalReason: '',
    notes: 'Sistema integrado com monitoramento inteligente de temperatura.',
    attachments: [],
    createdAt: new Date().toISOString(),
    lastUpdatedAt: new Date().toISOString(),
  },
  {
    id: createId(),
    name: 'Parque de notebooks comerciais',
    serialNumber: 'NBK-LOT-2023',
    brand: 'Lenovo',
    tag: 'PAT-055',
    status: 'OPERATING',
    categoryId: seedCategories[0].id,
    locationId: seedLocations[0].id,
    acquisitionDate: '2023-07-05T00:00:00.000Z',
    acquisitionDatePrecision: 'day',
    acquisitionCost: 152000,
    depreciationRate: 20,
    usefulLifeMonths: 36,
    warrantyEnd: '2026-07-05T00:00:00.000Z',
    custodian: 'Equipe de Suporte',
    supplier: 'Global Notebooks',
    entryOrigin: 'Compra',
    invoiceNumber: 'NF-2023-055',
    disposalDate: null,
    disposalReason: '',
    notes: 'Lote de notebooks destinados às áreas administrativas.',
    attachments: [],
    createdAt: new Date().toISOString(),
    lastUpdatedAt: new Date().toISOString(),
  },
];

const seedMaintenanceEntries = [
  {
    id: createId(),
    assetId: seedAssets[0].id,
    type: 'PREVENTIVE',
    status: 'COMPLETED',
    scheduledAt: '2024-09-10T13:00:00.000Z',
    performedAt: '2024-09-10T13:00:00.000Z',
    responsible: 'Bruno Lima',
    cost: 3200,
    notes: 'Atualização de firmware e limpeza preventiva.',
    createdAt: new Date().toISOString(),
  },
  {
    id: createId(),
    assetId: seedAssets[0].id,
    type: 'PREVENTIVE',
    status: 'SCHEDULED',
    scheduledAt: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000).toISOString(),
    performedAt: null,
    responsible: 'Equipe de Infraestrutura',
    cost: 2500,
    notes: 'Revisão trimestral com testes de contingência.',
    createdAt: new Date().toISOString(),
  },
  {
    id: createId(),
    assetId: seedAssets[1].id,
    type: 'CORRECTIVE',
    status: 'SCHEDULED',
    scheduledAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    performedAt: null,
    responsible: 'Fornecedor LiftMax',
    cost: 4100,
    notes: 'Substituição das correntes e calibração de segurança.',
    createdAt: new Date().toISOString(),
  },
  {
    id: createId(),
    assetId: seedAssets[2].id,
    type: 'PREVENTIVE',
    status: 'COMPLETED',
    scheduledAt: '2024-05-15T09:00:00.000Z',
    performedAt: '2024-05-16T15:00:00.000Z',
    responsible: 'ClimaTech Serviços',
    cost: 5800,
    notes: 'Manutenção com troca de filtros e limpeza das serpentinas.',
    createdAt: new Date().toISOString(),
  },
];

const getSeedState = () => ({
  categories: clone(seedCategories),
  locations: clone(seedLocations),
  assets: clone(seedAssets),
  maintenanceEntries: clone(seedMaintenanceEntries),
  reminderDefaults: { userIds: [], sectorIds: [] },
});

const isBrowser = typeof window !== 'undefined' && Boolean(window.localStorage);

const loadStoredState = () => {
  if (!isBrowser) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (
      !Array.isArray(parsed.categories)
      || !Array.isArray(parsed.locations)
      || !Array.isArray(parsed.assets)
      || !Array.isArray(parsed.maintenanceEntries)
    ) {
      return null;
    }
    return parsed;
  } catch (error) {
    console.error('Erro ao carregar dados de patrimônio do armazenamento local:', error);
    return null;
  }
};

const initialState = loadStoredState() || getSeedState();

let categories = initialState.categories;
let locations = initialState.locations;
let assets = initialState.assets;
let maintenanceEntries = initialState.maintenanceEntries;
let reminderDefaults = initialState.reminderDefaults || { userIds: [], sectorIds: [] };
let serverSyncPromise = null;
let mutationQueue = Promise.resolve();
let lastServerSyncAt = 0;
const SERVER_SYNC_CACHE_MS = 15_000;

const normalizeIdArray = (value) => {
  const values = Array.isArray(value) ? value : value ? [value] : [];
  return Array.from(new Set(values.map((item) => String(item)).filter(Boolean)));
};

const normalizeReminderDefaults = (value) => ({
  userIds: normalizeIdArray(value?.userIds),
  sectorIds: normalizeIdArray(value?.sectorIds),
});

const stateToPersist = () => ({
  categories,
  locations,
  assets,
  maintenanceEntries,
  reminderDefaults,
});

const syncFromServer = async ({ force = false } = {}) => {
  const cacheIsFresh = Date.now() - lastServerSyncAt < SERVER_SYNC_CACHE_MS;
  if (!force && cacheIsFresh) return true;
  if (serverSyncPromise) return serverSyncPromise;

  serverSyncPromise = (async () => {
    try {
      const response = await api.get('/assets/state?includeAttachments=false');
      const remoteState = response?.data?.data;
      if (
        remoteState
        && Array.isArray(remoteState.categories)
        && Array.isArray(remoteState.locations)
        && Array.isArray(remoteState.assets)
        && Array.isArray(remoteState.maintenanceEntries)
      ) {
        categories = remoteState.categories;
        locations = remoteState.locations;
        assets = remoteState.assets;
        maintenanceEntries = remoteState.maintenanceEntries;
        reminderDefaults = normalizeReminderDefaults(remoteState.reminderDefaults);
        lastServerSyncAt = Date.now();
        persistState();
        return true;
      }
      return false;
    } catch (_error) {
      return false;
    } finally {
      serverSyncPromise = null;
    }
  })();

  return serverSyncPromise;
};

const getApiErrorMessage = (error) =>
  error?.response?.data?.error
  || error?.response?.data?.message
  || error?.message
  || 'Não foi possível concluir a operação.';

const persistServerState = async () => {
  try {
    await api.put('/assets/state', stateToPersist());
    lastServerSyncAt = Date.now();
  } catch (error) {
    const status = error?.response?.status;
    if (status === 400 || status === 403) {
      throw new Error(getApiErrorMessage(error));
    }
  }
};

const stateForLocalStorage = () => ({
  categories,
  locations,
  assets: assets.map((asset) => ({
    ...asset,
    attachments: Array.isArray(asset.attachments)
      ? asset.attachments.map(({ url: _url, ...metadata }) => metadata)
      : [],
  })),
  maintenanceEntries,
  reminderDefaults,
});

const persistState = () => {
  if (!isBrowser) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stateForLocalStorage()));
  } catch (error) {
    console.error('Erro ao persistir dados de patrimônio no armazenamento local:', error);
  }
};

const buildMaintenance = (entry) => ({
  ...entry,
});

const normalizeAssetTag = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const ensureUniqueAssetTag = (tag, ignoredAssetId = null) => {
  const normalizedTag = normalizeAssetTag(tag);
  if (!normalizedTag) return;

  const duplicate = assets.find(
    (asset) => asset.id !== ignoredAssetId && normalizeAssetTag(asset.tag) === normalizedTag,
  );
  if (duplicate) {
    throw new Error(`Já existe um patrimônio cadastrado com a etiqueta "${tag}".`);
  }
};

const attachRelations = (asset) => {
  const category = categories.find((candidate) => candidate.id === asset.categoryId) || null;
  const location = locations.find((candidate) => candidate.id === asset.locationId) || null;
  const maintenance = maintenanceEntries
    .filter((entry) => entry.assetId === asset.id)
    .slice()
    .sort((a, b) => {
      const dateA = new Date(a.scheduledAt || a.performedAt || 0).getTime();
      const dateB = new Date(b.scheduledAt || b.performedAt || 0).getTime();
      return dateB - dateA;
    })
    .map(buildMaintenance);

  return {
    ...asset,
    category,
    location,
    maintenance,
  };
};

const attachAllRelations = () => {
  const categoryById = new Map(categories.map((category) => [category.id, category]));
  const locationById = new Map(locations.map((location) => [location.id, location]));
  const maintenanceByAssetId = new Map();

  maintenanceEntries.forEach((entry) => {
    const entries = maintenanceByAssetId.get(entry.assetId) || [];
    entries.push(buildMaintenance(entry));
    maintenanceByAssetId.set(entry.assetId, entries);
  });
  maintenanceByAssetId.forEach((entries) => entries.sort((a, b) => {
    const dateA = new Date(a.scheduledAt || a.performedAt || 0).getTime();
    const dateB = new Date(b.scheduledAt || b.performedAt || 0).getTime();
    return dateB - dateA;
  }));

  return assets.map((asset) => ({
    ...asset,
    category: categoryById.get(asset.categoryId) || null,
    location: locationById.get(asset.locationId) || null,
    maintenance: maintenanceByAssetId.get(asset.id) || [],
  }));
};

const respond = (payload) => ({ data: clone(payload) });

const restoreState = (snapshot) => {
  categories = snapshot.categories;
  locations = snapshot.locations;
  assets = snapshot.assets;
  maintenanceEntries = snapshot.maintenanceEntries;
  reminderDefaults = normalizeReminderDefaults(snapshot.reminderDefaults);
  persistState();
};

const executeOperation = async (callback, shouldPersist) => {
  await syncFromServer();
  const snapshot = shouldPersist ? clone(stateToPersist()) : null;
  const result = await callback();
  if (shouldPersist) {
    try {
      await persistServerState();
    } catch (error) {
      restoreState(snapshot);
      throw error;
    }
  }
  return respond(result);
};

const withDelay = (callback, _delay = 0, shouldPersist = true) => {
  if (!shouldPersist) return executeOperation(callback, false);

  const queuedOperation = mutationQueue.then(
    () => executeOperation(callback, true),
    () => executeOperation(callback, true),
  );
  mutationQueue = queuedOperation.then(() => undefined, () => undefined);
  return queuedOperation;
};

const mergeHydratedAttachments = (currentAttachments, remoteAttachments) => {
  const current = Array.isArray(currentAttachments) ? currentAttachments : [];
  const remote = Array.isArray(remoteAttachments) ? remoteAttachments : [];
  const currentById = new Map(current.map((attachment) => [String(attachment?.id), attachment]));

  remote.forEach((attachment) => {
    const key = String(attachment?.id || '');
    const existing = currentById.get(key);
    if (existing) {
      Object.assign(existing, attachment);
    } else if (attachment && typeof attachment === 'object') {
      current.push(attachment);
    }
  });

  return current;
};

const hydrateAssetFromServer = async (id) => {
  const response = await api.get(`/assets/${id}`);
  const remoteAsset = response?.data?.data;
  if (!remoteAsset?.id) return null;

  const index = assets.findIndex((asset) => asset.id === remoteAsset.id);
  if (index >= 0) {
    const current = assets[index];
    const attachments = mergeHydratedAttachments(current.attachments, remoteAsset.attachments);
    const storedAsset = { ...remoteAsset, attachments };
    delete storedAsset.category;
    delete storedAsset.location;
    delete storedAsset.maintenance;
    Object.assign(current, storedAsset);
    persistState();
    return current;
  }

  const storedAsset = { ...remoteAsset };
  delete storedAsset.category;
  delete storedAsset.location;
  delete storedAsset.maintenance;
  assets.push(storedAsset);
  persistState();
  return storedAsset;
};

const AssetService = {
  getSnapshot() {
    return withDelay(() => ({
      assets: attachAllRelations(),
      categories: categories.slice(),
      locations: locations.slice(),
      reminderDefaults: normalizeReminderDefaults(reminderDefaults),
    }), 0, false);
  },
  listAssets() {
    return withDelay(() => attachAllRelations(), 0, false);
  },
  getAsset(id) {
    const cachedAsset = assets.find((candidate) => candidate.id === id);
    if (cachedAsset) {
      void hydrateAssetFromServer(id).catch(() => undefined);
      return Promise.resolve({ data: attachRelations(cachedAsset) });
    }

    return withDelay(async () => {
      try {
        const hydratedAsset = await hydrateAssetFromServer(id);
        if (hydratedAsset) return attachRelations(hydratedAsset);
      } catch (error) {
        if (error?.response?.status === 404) {
          throw new Error('Ativo não encontrado.');
        }
      }
      const asset = assets.find((candidate) => candidate.id === id);
      if (!asset) throw new Error('Ativo não encontrado.');
      return attachRelations(asset);
    }, 0, false);
  },
  getReminderDefaults() {
    return withDelay(() => normalizeReminderDefaults(reminderDefaults), 160, false);
  },
  updateReminderDefaults(payload) {
    return withDelay(() => {
      reminderDefaults = normalizeReminderDefaults(payload);
      persistState();
      return reminderDefaults;
    });
  },
  createAsset(payload) {
    return withDelay(() => {
      const now = new Date().toISOString();
      const tag = payload?.tag?.trim() || '';
      const status = payload?.status || 'OPERATING';
      const isDisposed = status === 'DISPOSED';
      ensureUniqueAssetTag(tag);
      const asset = {
        id: createId(),
        name: payload?.name?.trim() || 'Ativo sem nome',
        serialNumber: payload?.serialNumber?.trim() || '',
        brand: payload?.brand?.trim() || '',
        tag,
        status,
        categoryId: payload?.categoryId || null,
        locationId: payload?.locationId || null,
        acquisitionDate: toDateISOString(payload?.acquisitionDate),
        acquisitionDatePrecision: getFlexibleDatePrecision(
          payload?.acquisitionDate,
          payload?.acquisitionDatePrecision || (payload?.acquisitionDate ? 'day' : null),
        ),
        acquisitionCost: Number(payload?.acquisitionCost) || 0,
        depreciationRate: Number(payload?.depreciationRate) || 0,
        usefulLifeMonths: payload?.usefulLifeMonths
          ? Number(payload.usefulLifeMonths)
          : null,
        warrantyEnd: toDateISOString(payload?.warrantyEnd),
        custodian: payload?.custodian?.trim() || '',
        responsibleUserIds: normalizeIdArray(payload?.responsibleUserIds || payload?.responsibleUserId),
        responsibleSectorIds: normalizeIdArray(payload?.responsibleSectorIds || payload?.responsibleSectorId),
        supplier: payload?.supplier?.trim() || '',
        entryOrigin: payload?.entryOrigin?.trim() || '',
        invoiceNumber: payload?.invoiceNumber?.trim() || '',
        disposalDate: isDisposed ? toDateISOString(payload?.disposalDate) : null,
        disposalReason: isDisposed ? payload?.disposalReason?.trim() || '' : '',
        notes: payload?.notes?.trim() || '',
        attachments: sanitizeAttachments(payload?.attachments),
        createdAt: now,
        lastUpdatedAt: now,
      };
      assets.push(asset);
      persistState();
      return attachRelations(asset);
    });
  },
  updateAsset(id, payload) {
    return withDelay(() => {
      const index = assets.findIndex((asset) => asset.id === id);
      if (index === -1) {
        throw new Error('Ativo não encontrado.');
      }
      const current = assets[index];
      const tag = payload?.tag?.trim() || '';
      const nextStatus = payload?.status || current.status;
      const isDisposed = nextStatus === 'DISPOSED';
      ensureUniqueAssetTag(tag, id);
      const updated = {
        ...current,
        name: payload?.name?.trim() || current.name,
        serialNumber: payload?.serialNumber?.trim() || '',
        brand:
          payload?.brand !== undefined
            ? payload.brand?.trim() || ''
            : current.brand || '',
        tag,
        status: nextStatus,
        categoryId: payload?.categoryId || null,
        locationId: payload?.locationId || null,
        acquisitionDate:
          payload?.acquisitionDate !== undefined
            ? toDateISOString(payload.acquisitionDate)
            : current.acquisitionDate,
        acquisitionDatePrecision:
          payload?.acquisitionDate !== undefined
            ? getFlexibleDatePrecision(
                payload.acquisitionDate,
                payload?.acquisitionDatePrecision || (payload.acquisitionDate ? 'day' : null),
              )
            : current.acquisitionDatePrecision || 'day',
        acquisitionCost:
          payload?.acquisitionCost !== undefined
            ? Number(payload.acquisitionCost) || 0
            : current.acquisitionCost,
        depreciationRate:
          payload?.depreciationRate !== undefined
            ? Number(payload.depreciationRate) || 0
            : current.depreciationRate,
        usefulLifeMonths:
          payload?.usefulLifeMonths !== undefined
            ? payload.usefulLifeMonths
              ? Number(payload.usefulLifeMonths)
              : null
            : current.usefulLifeMonths,
        warrantyEnd:
          payload?.warrantyEnd !== undefined
            ? toDateISOString(payload.warrantyEnd)
            : current.warrantyEnd,
        custodian:
          payload?.custodian !== undefined
            ? payload.custodian?.trim() || ''
            : current.custodian,
        responsibleUserIds:
          payload?.responsibleUserIds !== undefined || payload?.responsibleUserId !== undefined
            ? normalizeIdArray(payload?.responsibleUserIds || payload?.responsibleUserId)
            : normalizeIdArray(current.responsibleUserIds || current.responsibleUserId),
        responsibleSectorIds:
          payload?.responsibleSectorIds !== undefined || payload?.responsibleSectorId !== undefined
            ? normalizeIdArray(payload?.responsibleSectorIds || payload?.responsibleSectorId)
            : normalizeIdArray(current.responsibleSectorIds || current.responsibleSectorId),
        supplier:
          payload?.supplier !== undefined
            ? payload.supplier?.trim() || ''
            : current.supplier,
        entryOrigin:
          payload?.entryOrigin !== undefined
            ? payload.entryOrigin?.trim() || ''
            : current.entryOrigin,
        invoiceNumber:
          payload?.invoiceNumber !== undefined
            ? payload.invoiceNumber?.trim() || ''
            : current.invoiceNumber || '',
        disposalDate: isDisposed
          ? payload?.disposalDate !== undefined
            ? toDateISOString(payload.disposalDate)
            : current.disposalDate || null
          : null,
        disposalReason: isDisposed
          ? payload?.disposalReason !== undefined
            ? payload.disposalReason?.trim() || ''
            : current.disposalReason || ''
          : '',
        notes:
          payload?.notes !== undefined
            ? payload.notes?.trim() || ''
            : current.notes,
        attachments:
          payload?.attachments !== undefined
            ? sanitizeAttachments(payload.attachments)
            : sanitizeAttachments(current.attachments),
        lastUpdatedAt: new Date().toISOString(),
      };
      assets[index] = updated;
      persistState();
      return attachRelations(updated);
    });
  },
  deleteAsset(id) {
    return withDelay(() => {
      const index = assets.findIndex((asset) => asset.id === id);
      if (index === -1) {
        throw new Error('Ativo não encontrado.');
      }
      assets.splice(index, 1);
      for (let i = maintenanceEntries.length - 1; i >= 0; i -= 1) {
        if (maintenanceEntries[i].assetId === id) {
          maintenanceEntries.splice(i, 1);
        }
      }
      persistState();
      return { success: true };
    });
  },
  listCategories() {
    return withDelay(() => categories.slice(), 160, false);
  },
  createCategory(payload) {
    return withDelay(() => {
      const category = {
        id: createId(),
        name: payload?.name?.trim() || 'Categoria sem nome',
        description: payload?.description?.trim() || '',
        color: payload?.color?.trim() || '#0ea5e9',
      };
      categories.push(category);
      persistState();
      return category;
    });
  },
  updateCategory(id, payload) {
    return withDelay(() => {
      const index = categories.findIndex((category) => category.id === id);
      if (index === -1) {
        throw new Error('Categoria não encontrada.');
      }
      categories[index] = {
        ...categories[index],
        name: payload?.name?.trim() || categories[index].name,
        description:
          payload?.description !== undefined
            ? payload.description?.trim() || ''
            : categories[index].description,
        color:
          payload?.color !== undefined
            ? payload.color?.trim() || '#0ea5e9'
            : categories[index].color,
      };
      persistState();
      return categories[index];
    });
  },
  deleteCategory(id) {
    return withDelay(() => {
      const used = assets.some((asset) => asset.categoryId === id);
      if (used) {
        throw new Error('Não é possível excluir a categoria porque há ativos associados.');
      }
      const index = categories.findIndex((category) => category.id === id);
      if (index === -1) {
        throw new Error('Categoria não encontrada.');
      }
      categories.splice(index, 1);
      persistState();
      return { success: true };
    });
  },
  listLocations() {
    return withDelay(() => locations.slice(), 160, false);
  },
  createLocation(payload) {
    return withDelay(() => {
      const location = {
        id: createId(),
        name: payload?.name?.trim() || 'Local sem nome',
        description: payload?.description?.trim() || '',
        responsible: payload?.responsible?.trim() || '',
        contact: payload?.contact?.trim() || '',
      };
      locations.push(location);
      persistState();
      return location;
    });
  },
  updateLocation(id, payload) {
    return withDelay(() => {
      const index = locations.findIndex((location) => location.id === id);
      if (index === -1) {
        throw new Error('Localização não encontrada.');
      }
      locations[index] = {
        ...locations[index],
        name: payload?.name?.trim() || locations[index].name,
        description:
          payload?.description !== undefined
            ? payload.description?.trim() || ''
            : locations[index].description,
        responsible:
          payload?.responsible !== undefined
            ? payload.responsible?.trim() || ''
            : locations[index].responsible,
        contact:
          payload?.contact !== undefined
            ? payload.contact?.trim() || ''
            : locations[index].contact,
      };
      persistState();
      return locations[index];
    });
  },
  deleteLocation(id) {
    return withDelay(() => {
      const used = assets.some((asset) => asset.locationId === id);
      if (used) {
        throw new Error(
          'Não é possível excluir a localização porque há ativos associados.',
        );
      }
      const index = locations.findIndex((location) => location.id === id);
      if (index === -1) {
        throw new Error('Localização não encontrada.');
      }
      locations.splice(index, 1);
      persistState();
      return { success: true };
    });
  },
  recordMaintenance(assetId, payload) {
    return withDelay(() => {
      const asset = assets.find((candidate) => candidate.id === assetId);
      if (!asset) {
        throw new Error('Ativo não encontrado para registrar a manutenção.');
      }
      const entry = {
        id: createId(),
        assetId,
        type: payload?.type || 'PREVENTIVE',
        status: payload?.status || 'SCHEDULED',
        recurring: Boolean(payload?.recurring),
        periodicityValue:
          payload?.periodicityValue !== undefined && payload?.periodicityValue !== null
            ? Number(payload.periodicityValue) || null
            : null,
        periodicityUnit: payload?.periodicityUnit || 'DAYS',
        scheduledAt: toDateISOString(payload?.scheduledAt),
        performedAt: toDateISOString(payload?.performedAt),
        responsible: payload?.responsible?.trim() || '',
        cost: Number(payload?.cost) || 0,
        notes: payload?.notes?.trim() || '',
        createdAt: new Date().toISOString(),
      };
      maintenanceEntries.push(entry);
      asset.lastUpdatedAt = new Date().toISOString();
      persistState();
      return attachRelations(asset);
    });
  },
  updateMaintenance(assetId, maintenanceId, payload) {
    return withDelay(() => {
      const asset = assets.find((candidate) => candidate.id === assetId);
      if (!asset) {
        throw new Error('Ativo não encontrado.');
      }
      const index = maintenanceEntries.findIndex(
        (entry) => entry.id === maintenanceId && entry.assetId === assetId,
      );
      if (index === -1) {
        throw new Error('Registro de manutenção não encontrado.');
      }
      const previousStatus = maintenanceEntries[index].status;
      maintenanceEntries[index] = {
        ...maintenanceEntries[index],
        type: payload?.type || maintenanceEntries[index].type,
        status: payload?.status || maintenanceEntries[index].status,
        recurring:
          payload?.recurring !== undefined
            ? Boolean(payload.recurring)
            : Boolean(maintenanceEntries[index].recurring || maintenanceEntries[index].periodicityValue),
        periodicityValue:
          payload?.periodicityValue !== undefined
            ? Number(payload.periodicityValue) || null
            : maintenanceEntries[index].periodicityValue || null,
        periodicityUnit:
          payload?.periodicityUnit !== undefined
            ? payload.periodicityUnit || 'DAYS'
            : maintenanceEntries[index].periodicityUnit || 'DAYS',
        scheduledAt:
          payload?.scheduledAt !== undefined
            ? toDateISOString(payload.scheduledAt)
            : maintenanceEntries[index].scheduledAt,
        performedAt:
          payload?.performedAt !== undefined
            ? toDateISOString(payload.performedAt)
            : maintenanceEntries[index].performedAt,
        responsible:
          payload?.responsible !== undefined
            ? payload.responsible?.trim() || ''
            : maintenanceEntries[index].responsible,
        cost:
          payload?.cost !== undefined
            ? Number(payload.cost) || 0
            : maintenanceEntries[index].cost,
        notes:
          payload?.notes !== undefined
            ? payload.notes?.trim() || ''
            : maintenanceEntries[index].notes,
      };

      const updatedEntry = maintenanceEntries[index];
      const justCompleted = previousStatus !== 'COMPLETED' && updatedEntry.status === 'COMPLETED';
      const recurrenceEnabled = Boolean(updatedEntry.recurring) && Number(updatedEntry.periodicityValue) > 0;
      const nextAlreadyExists = maintenanceEntries.some(
        (entry) => entry.recurrenceSourceId === updatedEntry.id,
      );

      if (justCompleted && recurrenceEnabled && !nextAlreadyExists) {
        const baseDate = updatedEntry.scheduledAt || updatedEntry.performedAt || new Date().toISOString();
        maintenanceEntries.push({
          id: createId(),
          assetId,
          type: updatedEntry.type || 'PREVENTIVE',
          status: 'SCHEDULED',
          recurring: true,
          periodicityValue: Number(updatedEntry.periodicityValue),
          periodicityUnit: updatedEntry.periodicityUnit || 'MONTHS',
          scheduledAt: addMaintenancePeriod(baseDate, updatedEntry.periodicityValue, updatedEntry.periodicityUnit),
          performedAt: null,
          responsible: updatedEntry.responsible || '',
          cost: Number(updatedEntry.cost) || 0,
          notes: updatedEntry.notes || '',
          recurrenceSourceId: updatedEntry.id,
          createdAt: new Date().toISOString(),
        });
      }
      asset.lastUpdatedAt = new Date().toISOString();
      persistState();
      return attachRelations(asset);
    });
  },
  deleteMaintenance(assetId, maintenanceId) {
    return withDelay(() => {
      const index = maintenanceEntries.findIndex(
        (entry) => entry.assetId === assetId && entry.id === maintenanceId,
      );
      if (index === -1) {
        throw new Error('Registro de manutenção não encontrado.');
      }
      maintenanceEntries.splice(index, 1);
      persistState();
      return { success: true };
    });
  },
};

export default AssetService;
